import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Wallet, Plus, Pencil, Trash2, Loader2, Copy, ExternalLink,
  QrCode, Building2, Smartphone, CreditCard, Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const methodTypeIcons = {
  gcash: Smartphone,
  maya: Smartphone,
  diskartech: Smartphone,
  bank_transfer: Building2,
  paypal: CreditCard,
  other: Wallet
};

const methodTypeLabels = {
  gcash: 'GCash',
  maya: 'Maya',
  diskartech: 'Diskartech',
  bank_transfer: 'Bank Transfer',
  paypal: 'PayPal',
  other: 'Other'
};

const PaymentMethodsManager = ({ eventId, token, isOrganizer = false, readOnly = false }) => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qrBlobUrls, setQrBlobUrls] = useState({});
  const [formData, setFormData] = useState({
    method_type: 'gcash',
    account_name: '',
    account_number: '',
    bank_name: '',
    qr_code_image: '',
    qr_code_file_id: '',
    payment_link: '',
    instructions: ''
  });

  useEffect(() => {
    if (eventId) {
      fetchMethods();
    }
  }, [eventId]);

  const fetchMethods = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/event/payment-methods?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMethods(response.data);
      
      // Load QR code images from cloud storage
      const newQrUrls = {};
      for (const method of response.data) {
        if (method.qr_code_file_id) {
          try {
            const qrResp = await axios.get(
              `${API_URL}/api/files/download/${method.qr_code_file_id}`,
              { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
            );
            newQrUrls[method.id] = URL.createObjectURL(qrResp.data);
          } catch (err) {
            console.error('Failed to load QR code:', err);
          }
        }
      }
      setQrBlobUrls(prev => {
        // Revoke old blob URLs
        Object.values(prev).forEach(url => URL.revokeObjectURL(url));
        return newQrUrls;
      });
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (method = null) => {
    if (method) {
      setEditingMethod(method);
      setFormData({
        method_type: method.method_type,
        account_name: method.account_name,
        account_number: method.account_number,
        bank_name: method.bank_name || '',
        qr_code_image: method.qr_code_image || '',
        qr_code_file_id: method.qr_code_file_id || '',
        payment_link: method.payment_link || '',
        instructions: method.instructions || ''
      });
    } else {
      setEditingMethod(null);
      setFormData({
        method_type: 'gcash',
        account_name: '',
        account_number: '',
        bank_name: '',
        qr_code_image: '',
        qr_code_file_id: '',
        payment_link: '',
        instructions: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.account_name || !formData.account_number) {
      toast.error('Account name and number are required');
      return;
    }

    setSaving(true);
    try {
      if (editingMethod) {
        await axios.put(
          `${API_URL}/api/event/payment-methods/${editingMethod.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Payment method updated');
      } else {
        await axios.post(
          `${API_URL}/api/event/payment-methods?event_id=${eventId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Payment method added');
      }
      setDialogOpen(false);
      fetchMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment method');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (methodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/event/payment-methods/${methodId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment method deleted');
      fetchMethods();
    } catch (error) {
      toast.error('Failed to delete payment method');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleQRUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB for QR codes)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('QR code image must be less than 2MB');
      return;
    }

    try {
      // Upload to cloud storage
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/files/upload/image?category=qrcodes&entity_id=${eventId}`,
        uploadFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Store the file ID in form data
      setFormData({ 
        ...formData, 
        qr_code_file_id: response.data.id,
        qr_code_image: '' // Clear base64 if any
      });
      
      toast.success('QR code uploaded');
    } catch (error) {
      console.error('QR upload error:', error);
      // Fallback to base64 if cloud upload fails
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, qr_code_image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-maroon" />
              Payment Methods
            </CardTitle>
            <CardDescription>
              {readOnly 
                ? 'Available payment options for this reunion' 
                : 'Manage payment accounts for attendees to pay'}
            </CardDescription>
          </div>
          {!readOnly && isOrganizer && (
            <Button onClick={() => openDialog()} className="bg-maroon hover:bg-maroon/90">
              <Plus className="h-4 w-4 mr-2" />
              Add Method
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {methods.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Wallet className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No payment methods configured</p>
            {!readOnly && isOrganizer && (
              <p className="text-sm mt-2">Add payment accounts so attendees know where to send payments</p>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {methods.map(method => {
              const Icon = methodTypeIcons[method.method_type] || Wallet;
              return (
                <Card key={method.id} className="border-2 hover:border-maroon/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-maroon/10 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-maroon" />
                        </div>
                        <div>
                          <Badge variant="outline" className="mb-1">
                            {methodTypeLabels[method.method_type] || method.method_type}
                          </Badge>
                          <p className="font-semibold text-gray-900">{method.account_name}</p>
                        </div>
                      </div>
                      {!readOnly && isOrganizer && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openDialog(method)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(method.id)} className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-600">Account #:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium">{method.account_number}</span>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(method.account_number)} className="h-6 w-6 p-0">
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      
                      {method.bank_name && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Bank:</span>
                          <span className="text-sm font-medium">{method.bank_name}</span>
                        </div>
                      )}
                      
                      {method.payment_link && (
                        <a 
                          href={method.payment_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <LinkIcon className="h-4 w-4" />
                          Open Payment Link
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      
                      {(method.qr_code_image || method.qr_code_file_id) && (
                        <div className="mt-3">
                          <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                            <QrCode className="h-4 w-4" />
                            Scan QR Code:
                          </p>
                          <img 
                            src={qrBlobUrls[method.id] || method.qr_code_image} 
                            alt="QR Code" 
                            className="w-32 h-32 border rounded-lg mx-auto"
                          />
                        </div>
                      )}
                      
                      {method.instructions && (
                        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                          <p className="font-medium mb-1">Instructions:</p>
                          <p>{method.instructions}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}
            </DialogTitle>
            <DialogDescription>
              Configure payment account details for attendees
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Payment Type *</Label>
              <Select
                value={formData.method_type}
                onValueChange={(value) => setFormData({ ...formData, method_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gcash">GCash</SelectItem>
                  <SelectItem value="maya">Maya</SelectItem>
                  <SelectItem value="diskartech">Diskartech</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Account Name *</Label>
              <Input
                value={formData.account_name}
                onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                placeholder="e.g., Juan Dela Cruz"
              />
            </div>

            <div className="space-y-2">
              <Label>Account Number *</Label>
              <Input
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                placeholder="e.g., 09171234567"
              />
            </div>

            {formData.method_type === 'bank_transfer' && (
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="e.g., BDO, BPI, Metrobank"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Payment Link (Optional)</Label>
              <Input
                value={formData.payment_link}
                onChange={(e) => setFormData({ ...formData, payment_link: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label>QR Code Image (Optional)</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleQRUpload}
              />
              {(formData.qr_code_image || formData.qr_code_file_id) && (
                <div className="flex items-center gap-2 mt-2">
                  {formData.qr_code_image && (
                    <img src={formData.qr_code_image} alt="QR Preview" className="w-24 h-24 border rounded" />
                  )}
                  {formData.qr_code_file_id && !formData.qr_code_image && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <QrCode className="h-4 w-4" />
                      QR code uploaded to cloud storage
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Instructions (Optional)</Label>
              <Textarea
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                placeholder="e.g., Please include your full name in the reference"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-maroon hover:bg-maroon/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingMethod ? 'Update' : 'Add'} Method
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PaymentMethodsManager;
